/** Alert feed. Reads the manifest and per-cell bundles straight from R2 through
 *  the Cloudflare edge cache — these requests never touch a Worker, which is what
 *  keeps the running cost flat as the user count grows. */

import { encodeGeohash, geohashNeighbours } from "../../shared/geohash.js";
import type { Alert } from "../../shared/alerts.js";
import type { TrafficItem } from "../../shared/traffic.js";

export type Manifest = { at: number; cells: Record<string, number>; ver?: Record<string, string> };

export class Feed {
  /** Version seen per cell (content hash when the server sends one, else the count). */
  private cellVersion = new Map<string, string>();
  private subscribed: string[] = [];
  private centre: string | null = null;
  lastSyncAt = 0;

  constructor(private readonly snapshotBase: string) {}

  /** Recomputed only when the driver crosses into a new gh5 cell, not per GPS tick. */
  cellsFor(lat: number, lon: number): { changed: boolean; cells: string[] } {
    const gh5 = encodeGeohash(lat, lon, 5);
    if (gh5 === this.centre) return { changed: false, cells: this.subscribed };
    this.centre = gh5;
    this.subscribed = geohashNeighbours(gh5);
    return { changed: true, cells: this.subscribed };
  }

  /** Fresh traffic per gh7:octant, replaced on every sync that carries traffic. */
  readonly traffic = new Map<string, TrafficItem>();

  async sync(lat: number, lon: number): Promise<{ alerts: Alert[]; stale: boolean }> {
    const { cells } = this.cellsFor(lat, lon);
    let manifest: Manifest;
    try {
      const res = await fetch(`${this.snapshotBase}/manifest.json`, { cache: "no-cache" });
      if (!res.ok) return { alerts: [], stale: true };
      manifest = (await res.json()) as Manifest;
    } catch {
      return { alerts: [], stale: true };
    }

    const versionOf = (c: string): string => manifest.ver?.[c] ?? String(manifest.cells[c]);
    const wanted = cells.filter((c) => manifest.cells[c] !== undefined);
    const changed = wanted.filter((c) => this.cellVersion.get(c) !== versionOf(c));

    const alerts: Alert[] = [];
    await Promise.all(
      changed.map(async (cell) => {
        try {
          const res = await fetch(`${this.snapshotBase}/alerts/${cell}.json`);
          if (!res.ok) return;
          const body = (await res.json()) as { items: RawItem[] };
          for (const it of body.items) alerts.push(expand(it));
          this.cellVersion.set(cell, versionOf(cell));
        } catch {
          /* One unreachable cell must not fail the whole sync. */
        }
      }),
    );

    // Traffic: same versioning, separate objects. A cell that vanished from the
    // manifest has no fresh samples, so its entries are dropped.
    const tKeys = cells.map((c) => `traffic/${c}`);
    for (const [k] of this.traffic) {
      const cell = k.slice(0, 5);
      if (cells.includes(cell) && manifest.cells[`traffic/${cell}`] === undefined) this.traffic.delete(k);
    }
    const tChanged = tKeys.filter((k) => manifest.cells[k] !== undefined && this.cellVersion.get(k) !== versionOf(k));
    await Promise.all(
      tChanged.map(async (k) => {
        try {
          const res = await fetch(`${this.snapshotBase}/${k}.json`);
          if (!res.ok) return;
          const body = (await res.json()) as { items: TrafficItem[] };
          const cell = k.slice("traffic/".length);
          for (const [tk] of this.traffic) if (tk.startsWith(cell)) this.traffic.delete(tk);
          for (const it of body.items) this.traffic.set(`${it.g}:${it.o}`, it);
          this.cellVersion.set(k, versionOf(k));
        } catch { /* one cell */ }
      }),
    );

    this.lastSyncAt = Date.now();
    return { alerts, stale: false };
  }
}

/** The wire format is deliberately terse — these bundles are fetched constantly. */
type RawItem = {
  i: string; t: string; la: number; lo: number; rad: number; c: number | null;
  n: number; cf: number; src: string; prov: string | null; txt: string | null;
  ca: number; ea: number; h?: number;
};

function expand(it: RawItem): Alert {
  return {
    id: it.i,
    type: it.t as Alert["type"],
    lat: it.la,
    lon: it.lo,
    radiusM: it.rad,
    reporterCourse: it.c,
    samples: it.n,
    confidence: it.cf,
    source: it.src as Alert["source"],
    provenance: it.prov,
    sourceText: it.txt,
    createdAt: it.ca,
    expiresAt: it.ea,
    hold: it.h === 1,
  };
}
